type DebugMethod = 'log' | 'info' | 'warn' | 'error';

const DEBUG_STORAGE_KEY = 'eruption-debug-logging';
const DEBUG_VERBOSE_STORAGE_KEY = 'eruption-debug-logging-verbose';

const parseBoolean = (value: string | null | undefined): boolean | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'on', 'enabled', 'yes'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'off', 'disabled', 'no'].includes(normalized)) {
    return false;
  }
  return undefined;
};

const getLocalPreference = (): boolean | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const stored = window.localStorage.getItem(DEBUG_STORAGE_KEY);
    return parseBoolean(stored);
  } catch {
    return undefined;
  }
};

const getLocalVerbosePreference = (): boolean | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const stored = window.localStorage.getItem(DEBUG_VERBOSE_STORAGE_KEY);
    return parseBoolean(stored);
  } catch {
    return undefined;
  }
};

const getInitialDebugState = (): boolean => {
  const envOverride = parseBoolean(process.env.REACT_APP_DEBUG_LOGGING);
  if (envOverride !== undefined) {
    return envOverride;
  }

  const localPreference = getLocalPreference();
  if (localPreference !== undefined) {
    return localPreference;
  }

  return process.env.NODE_ENV !== 'production';
};

let debugEnabled = getInitialDebugState();
let verboseEnabled =
  parseBoolean(process.env.REACT_APP_DEBUG_VERBOSE_LOGGING ?? process.env.REACT_APP_DEBUG_VERBOSE) ??
  getLocalVerbosePreference() ??
  false;

export const isDebugLoggingEnabled = (): boolean => debugEnabled;
export const isDebugVerboseEnabled = (): boolean => debugEnabled && verboseEnabled;

export const setDebugLoggingEnabled = (enabled: boolean): void => {
  debugEnabled = enabled;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(enabled));
      (window as typeof window & { __setEruptionDebugLogging?: (value: boolean) => void }).__setEruptionDebugLogging =
        setDebugLoggingEnabled;
    } catch {
      // Ignore storage errors (e.g. private mode)
    }
  }
};

export const setDebugVerboseEnabled = (enabled: boolean): void => {
  verboseEnabled = enabled;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(DEBUG_VERBOSE_STORAGE_KEY, JSON.stringify(enabled));
      (window as typeof window & { __setEruptionDebugVerboseLogging?: (value: boolean) => void }).__setEruptionDebugVerboseLogging =
        setDebugVerboseEnabled;
    } catch {
      // Ignore storage errors
    }
  }
};

// Ensure helper is available immediately in browser devtools
if (typeof window !== 'undefined') {
  (window as typeof window & { __setEruptionDebugLogging?: (value: boolean) => void }).__setEruptionDebugLogging =
    setDebugLoggingEnabled;
  (window as typeof window & { __setEruptionDebugVerboseLogging?: (value: boolean) => void }).__setEruptionDebugVerboseLogging =
    setDebugVerboseEnabled;
}

function debugConsole(method: DebugMethod, ...args: unknown[]): void {
  if (!debugEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console[method](...args);
}

export function debugLog(...args: unknown[]): void {
  debugConsole('log', ...args);
}

export function debugInfo(...args: unknown[]): void {
  debugConsole('info', ...args);
}

export function debugWarn(...args: unknown[]): void {
  debugConsole('warn', ...args);
}

export function debugError(...args: unknown[]): void {
  debugConsole('error', ...args);
}

export function debugVerbose(...args: unknown[]): void {
  if (!isDebugVerboseEnabled()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(...args);
}

export function debugGroup(label: string, callback: () => void): void {
  if (!debugEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console.group(label);
  try {
    callback();
  } finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}
